import { Component } from 'react';
import { connect } from 'react-redux';
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import AwsS3Multipart from '@uppy/aws-s3-multipart';
import DragDrop from '@uppy/drag-drop';
import FileInput from '@uppy/file-input';
import ThumbnailGenerator from '@uppy/thumbnail-generator';
import XHRUpload from '@uppy/xhr-upload';
import JSZip from 'jszip';
import { v4 as uuidv4 } from 'uuid';
import api from 'api';
import config from 'config';
import { getSafeFilename } from 'shared-helpers/common';
import { getFormattedSize } from 'lib/common';
import { validateH5P } from 'lib/h5p';
import {
  FileUploaderButton,
  FileUploaderDragDrop,
  FileUploaderDragDropActions,
  FileUploaderDragDropTitle,
  FileUploaderDragDropZipHandler,
  FileUploaderInProgress,
  FileUploaderUploading
} from '.';

const FileUploader = connect((state) => state)(
  class extends Component {
    constructor(props) {
      super(props);

      this.state = this.getInitialState();

      this.uppy = Uppy({
        debug: true,
        id: `uppy-${props.id}`,
        restrictions: {
          allowedFileTypes: this.props.allowedFileTypes,
          maxFileSize:
            this.props.maxFileSize || (this.props.useMultipart ? 10e9 : 5e9),
          maxNumberOfFiles: 1
        },
        autoProceed:
          this.props.autoProceed !== undefined ? this.props.autoProceed : false,
        onBeforeUpload: (files) => {
          if (!Object.keys(files).length) {
            return false;
          }
        }
      });

      if (this.props.getUploader) this.props.getUploader(this);
    }

    componentDidMount() {
      if (this.props.dragDrop) {
        this.uppy.use(DragDrop, {
          id: `file-input-${this.props.id}`,
          target: this.uppyElement
          // locale: {
          //   strings: {
          //     chooseFiles: ''
          //   }
          // }
        });
      } else {
        this.uppy.use(FileInput, {
          id: `file-input-${this.props.id}`,
          target: this.uppyElement,
          pretty: true,
          locale: {
            strings: {
              chooseFiles: ''
            }
          }
        });
      }

      if (this.props.destination === 'local') {
        this.uppy.use(XHRUpload, {
          endpoint: `${config.apiUrl}${
            this.props.endpoint || '/admin/import/upload'
          }`,
          formData: true,
          fieldName: 'file',
          id: `xhr-upload-${this.props.id}`,
          withCredentials: true
        });
      } else if (this.props.useMultipart) {
        this.uppy.use(AwsS3Multipart, {
          createMultipartUpload: async (file) => {
            let key = this.props.keyPrefix;

            const uuid = uuidv4();
            if (this.props.uuidAsFilename) {
              key =
                `${key}${uuid}` + (file.extension ? `.${file.extension}` : '');
            } else {
              key = `${key}${uuid}/${getSafeFilename(file.name)}`;
            }

            const contentType = file.type;

            const response = await api.post('/s3/create-multipart-upload', {
              key,
              contentType
            });

            return {
              uploadId: response.data.uploadId,
              key: response.data.key
            };
          },
          listParts: async (file, { key, uploadId }) => {
            const response = await api.get(
              '/s3/get-uploaded-parts' +
                `?key=${encodeURIComponent(key)}` +
                `&uploadId=${encodeURIComponent(uploadId)}`
            );
            return response.data.parts;
          },
          prepareUploadPart: async (file, { key, uploadId, number }) => {
            const response = await api.get(
              '/s3/sign-part-upload' +
                `?key=${encodeURIComponent(key)}` +
                `&uploadId=${encodeURIComponent(uploadId)}` +
                `&partNumber=${encodeURIComponent(number)}`
            );
            return { url: response.data.url };
          },
          abortMultipartUpload: async (file, { key, uploadId }) => {
            await api.delete(
              '/s3/abort-multipart-upload' +
                `?key=${encodeURIComponent(key)}` +
                `&uploadId=${encodeURIComponent(uploadId)}`
            );
          },
          completeMultipartUpload: async (file, { key, uploadId, parts }) => {
            const response = await api.post('/s3/complete-multipart-upload', {
              key,
              uploadId,
              parts
            });
            return {
              location:
                `${this.props.serverConfig.s3CloudFrontHostname}/` +
                `${response.data.key}`
            };
          }
        });
      } else {
        this.uppy.use(AwsS3, {
          getUploadParameters: async (file) => {
            let key = this.props.keyPrefix;

            if (
              this.props.askToDecompressZip &&
              this.state.linkType === 'html'
            ) {
              key = 'uploads/compressed/';
            }

            const uuid = uuidv4();
            if (this.props.uuidAsFilename) {
              key =
                `${key}${uuid}` + (file.extension ? `.${file.extension}` : '');
            } else {
              key = `${key}${uuid}/${getSafeFilename(file.name)}`;
            }

            const contentType = file.type;

            const response = await api.post('/s3/sign-upload-request', {
              key,
              contentType
            });

            return {
              method: response.data.method,
              url: response.data.url,
              fields: response.data.fields
            };
          },
          id: `aws-s3-${this.props.id}`
        });
      }

      this.uppy.on('restriction-failed', (file, error) => {
        if (
          error.message.indexOf('This file exceeds maximum allowed size') === 0
        ) {
          this.setState({
            fileError:
              this.props.fileSizeErrorText ||
              this.props.messages.uploaderFileSizeError
          });
        } else {
          this.setState({
            fileError:
              this.props.fileTypeErrorText ||
              this.props.messages.uploaderFileTypeError
          });
        }
      });

      this.uppy.on('file-added', async (file) => {
        if (this.props.isH5P) {
          try {
            const zip = await JSZip.loadAsync(file.data);
            if (!(await validateH5P(zip))) this.props.onError();
          } catch (err) {
            this.props.onError();
          }
        }

        const fileAddedState = (await this.props.validateZipContent?.(
          file
        )) || {
          file,
          fileError: null,
          htmlFile: null,
          htmlFiles: [],
          autoSelectHtml: false,
          linkType: 'direct',
          title: file.name
        };

        this.setState(fileAddedState);
        if (fileAddedState.autoSelectHtml) this.handleUpload();
        if (this.props.onFileAdded)
          this.props.onFileAdded({ filename: file.name });
      });

      this.uppy.on('upload', () => {
        this.setState({ uploading: true });
      });

      this.uppy.on('upload-progress', (file, progress) => {
        this.setState({
          progress: progress.bytesUploaded / progress.bytesTotal
        });
      });

      this.uppy.on('upload-success', (file, response) => {
        if (!this.props.onUploadSuccess) return;

        if (this.props.destination === 'local') {
          this.props.onUploadSuccess({
            extension: file.extension,
            filename: file.name,
            filePath: response.body.filePath,
            scormManifest: this.state.scormManifest,
            latestUploadFile: this.state.latestUploadFile,
            manifestUrl: this.state.manifestUrl,
            response: response.body
          });
        } else {
          const url = this.props.useMultipart
            ? response.uploadURL
            : `${this.props.serverConfig.s3CloudFrontHostname}/${file.meta.key}`;
          this.props.onUploadSuccess({
            extension: file.extension,
            filename: file.name,
            htmlFile: this.state.htmlFile,
            scormManifest: this.state.scormManifest,
            latestUploadFile: this.state.latestUploadFile,
            manifestUrl: this.state.manifestUrl,
            linkType: this.state.linkType,
            title: this.state.title || file.name,
            url: url
          });
        }
      });

      this.uppy.on('upload-error', (file, error, response) => {
        this.setState({ progress: 0, uploading: false });
        if (this.props.onError)
          this.props.onError({ error, response: response?.body });
      });

      if (this.props.onThumbnailGenerated) {
        this.uppy.use(ThumbnailGenerator, {
          id: `thumbnail-generator-${this.props.id}`,
          thumbnailWidth: 200
        });
        this.uppy.on('thumbnail:generated', (file, preview) => {
          this.props.onThumbnailGenerated({ preview });
        });
      }
    }

    componentWillUnmount() {
      this.uppy.close();
    }

    getInitialState = () => {
      return {
        file: this.props.initialFilename
          ? { name: this.props.initialFilename }
          : null,
        fileError: null,
        initialFilename: this.props.initialFilename,
        htmlFile: null,
        htmlFiles: [],
        linkType: 'direct',
        progress: 0,
        title: '',
        uploading: false
      };
    };

    handleHtmlFileChange = (htmlFile) => {
      this.setState({ htmlFile });
    };

    handleLinkTypeChange = (linkType) => {
      this.setState({ linkType });
    };

    handleRemove = () => {
      this.setState({ file: null, htmlFiles: [], initialFilename: null });
      if (this.state.file) {
        this.uppy.removeFile(this.state.file.id);
        if (this.props.onFileRemoved) this.props.onFileRemoved();
      }
    };

    handleTitleChange = (title) => {
      this.setState({ title });
    };

    handleUpload = () => {
      // upload() skips a file that already failed, so a retry needs retryAll()
      const hasFailedFile = this.uppy.getFiles().some((file) => file.error);
      if (hasFailedFile) this.uppy.retryAll();
      else this.uppy.upload();
    };

    reset = () => {
      const initialState = this.getInitialState();
      this.setState(initialState);
      if (this.state.file) {
        this.uppy.removeFile(this.state.file.id);
        if (this.props.onFileRemoved) this.props.onFileRemoved();
      }
    };

    upload = async (sendNotice = true) => {
      const result = await this.uppy.upload();

      if (!result || result.failed.length > 0) {
        if (sendNotice) {
          // this will send an error notice for the user that the
          // upload failed but will not send an error to the server
          this.props.dispatch.handleErrorDispatch(
            null,
            this.props.messages.commonUploadFileFailed
          );
        }
        return null;
      }

      const files = result.successful.map((file) => {
        if (this.props.destination === 'local') {
          return {
            extension: file.extension,
            filename: file.name,
            filePath: file.response.body.filePath,
            scormManifest: this.state.scormManifest,
            latestUploadFile: this.state.latestUploadFile,
            manifestUrl: this.state.manifestUrl
          };
        } else {
          return {
            extension: file.extension,
            filename: file.name,
            htmlFile: this.state.htmlFile,
            scormManifest: this.state.scormManifest,
            latestUploadFile: this.state.latestUploadFile,
            manifestUrl: this.state.manifestUrl,
            linkType: this.state.linkType,
            size: file.size,
            title: this.state.title || file.name,
            type: file.type,
            url: this.props.useMultipart
              ? file.response.uploadURL
              : `${this.props.serverConfig.s3CloudFrontHostname}/${file.meta.key}`
          };
        }
      });

      return files.length > 1 ? files : files[0];
    };

    render() {
      const {
        askForTitle,
        askToDecompressZip,
        autoProceed,
        blockType,
        buttonDarkStyle,
        disabled,
        dragDrop,
        dragDropActions = true,
        dragDropCompactStyle,
        fontSize = 'medium',
        icon,
        id,
        infoText,
        inProgress,
        inProgressText,
        messages,
        placeholder,
        style,
        theme,
        uploadActionText
      } = this.props;

      const disabledUpload =
        this.props.initialFilename &&
        this.state.file?.name === this.props.initialFilename;

      return (
        <div className="uploader" style={style}>
          {inProgress ? (
            <FileUploaderInProgress theme={theme} text={inProgressText} />
          ) : dragDrop && this.state.uploading && this.state.file ? (
            <FileUploaderUploading
              filename={this.state.file.name}
              messages={messages}
              progress={this.state.progress}
              size={getFormattedSize(this.state.file.size)}
              theme={theme}
            />
          ) : dragDrop ? (
            <>
              <FileUploaderDragDrop
                compactStyle={dragDropCompactStyle}
                icon={icon}
                infoText={infoText}
                file={this.state.file}
                fileError={this.state.fileError}
                fontSize={fontSize}
                onRemove={this.handleRemove}
                placeholder={placeholder || messages.uploaderChooseFileOrDrag}
                showRemove={!dragDropActions}
                size={
                  this.state.file?.size
                    ? getFormattedSize(this.state.file.size)
                    : null
                }
                uppyElementRef={(el) => (this.uppyElement = el)}
              />
              {this.state.file && !autoProceed ? (
                <>
                  {askForTitle ? (
                    <FileUploaderDragDropTitle
                      file={this.state.file}
                      id={id}
                      messages={messages}
                      onChange={this.handleTitleChange}
                    />
                  ) : null}
                  {askToDecompressZip &&
                  !this.state.autoSelectHtml &&
                  this.state.htmlFiles.length ? (
                    <FileUploaderDragDropZipHandler
                      htmlFile={this.state.htmlFile}
                      htmlFiles={this.state.htmlFiles}
                      id={id}
                      linkType={this.state.linkType}
                      messages={messages}
                      onHtmlFileChange={this.handleHtmlFileChange}
                      onLinkTypeChange={this.handleLinkTypeChange}
                    />
                  ) : null}
                  {dragDropActions ? (
                    <FileUploaderDragDropActions
                      controlUploadDisabled={disabledUpload}
                      messages={messages}
                      onCancel={this.handleRemove}
                      onUpload={this.handleUpload}
                      uploadText={uploadActionText || messages.commonUploadFile}
                    />
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <FileUploaderButton
              darkStyle={buttonDarkStyle}
              disabled={disabled}
              file={this.state.file}
              initialFilename={this.state.initialFilename}
              onRemove={this.handleRemove}
              placeholder={placeholder || messages.commonUploadFile}
              theme={theme}
              uppyElementRef={(el) => (this.uppyElement = el)}
            />
          )}

          <style jsx>{`
            .uploader {
              background-color: ${theme.lightGrey};
              border-radius: ${theme.cardBorderRadius};
              padding: ${dragDrop ? '15px' : '0'};
            }
            .uppy :global(.uppy-DragDrop-container) {
              background-color: #fff;
              border: 2px dashed #999;
              border-radius: 3px;
              cursor: pointer;
              margin: 0;
              opacity: 0.3;
              padding: 0;
              position: absolute;
              top: 0;
              bottom: 0;
              left: 0;
              right: 0;
              transition: border 0.3s;
              z-index: ${this.state.initialFilename || this.state.file
                ? -1
                : 1};
            }
            .uppy :global(.uppy-DragDrop-container:hover) {
              border-color: #000;
            }
            .uppy :global(.uppy-DragDrop--isDraggingOver) {
              background-color: #ddd;
              border-color: #000;
            }
            .uppy :global(.uppy-DragDrop-input) {
              opacity: 0;
              overflow: hidden;
              position: absolute;
              height: 0.1px;
              width: 0.1px;
              z-index: -1;
            }
            .uppy :global(.uppy-DragDrop-inner) {
              display: none;
            }
          `}</style>
        </div>
      );
    }
  }
);

export default FileUploader;
